import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  AlertCircle,
  CheckCircle,
  HelpCircle,
  Star,
  ArrowLeft,
  Hash,
  Globe
} from 'lucide-react';
import { BASE_URL } from '../config';

export default function FAQ({ onBack, onNavigate }) {
  const navigate = useNavigate();
  const [faqItems, setFaqItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState(null);

  // Page choices for FAQ association
  const pageChoices = [
    { value: 'home', label: 'Home Page' },
    { value: 'features', label: 'Features Page' },
    { value: 'pricing', label: 'Pricing Page' },
    { value: 'blog', label: 'Blog Page' },
    { value: 'contact', label: 'Contact Page' },
    { value: 'about', label: 'About Us' },
    { value: 'book-doctor', label: 'Book Doctor' },
    { value: 'doctors', label: 'Doctors' },
    { value: 'hospitals', label: 'Hospitals' },
    { value: 'appointments', label: 'Appointments' },
    { value: 'chat', label: 'Chat' },
    { value: 'ai-chat', label: 'AI Chat' },
    { value: 'ai-assistant', label: 'AI Assistant' }
  ];

  // Fetch FAQ items
  const fetchFaqItems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        showNotification('Authentication token not found', 'error');
        setFaqItems([]);
        return;
      }

      console.log('🔍 Fetching FAQ items from:', `${BASE_URL}/api/seo/admin/faq/list/`);
      
      const response = await fetch(`${BASE_URL}/api/seo/admin/faq/list/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ FAQ data received:', data);
        setFaqItems(data.faq_items || []);
        
        if (data.faq_items && data.faq_items.length > 0) {
          console.log(`📊 Loaded ${data.faq_items.length} FAQ items`);
        } else {
          console.log('📝 No FAQ items found - this is normal for a new system');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ API Error:', response.status, errorData);
        
        if (response.status === 401 || response.status === 403) {
          showNotification('Authentication failed. Please login again.', 'error');
        } else {
          showNotification(errorData.error || `Failed to fetch FAQ items (${response.status})`, 'error');
        }
        setFaqItems([]);
      }
    } catch (error) {
      console.error('❌ Network Error fetching FAQ items:', error);
      showNotification('Network error. Please check your connection.', 'error');
      setFaqItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Delete FAQ item
  const deleteFaqItem = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ item?')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        showNotification('Authentication token not found', 'error');
        return;
      }

      console.log('🗑️ Deleting FAQ item:', faqId);

      const response = await fetch(`${BASE_URL}/api/seo/admin/faq/${faqId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        showNotification('FAQ item deleted successfully');
        fetchFaqItems(); // Refresh the list
      } else {
        const errorData = await response.json().catch(() => ({}));
        showNotification(errorData.error || 'Failed to delete FAQ item', 'error');
      }
    } catch (error) {
      console.error('Error deleting FAQ item:', error);
      showNotification('Network error. Please check your connection.', 'error');
    }
  };

  // Open modal/navigate
  const openModal = (mode, faq = null) => {
    console.log('🔍 openModal called with:', { mode, faq, onNavigate: !!onNavigate });
    
    const navigateFunction = onNavigate || navigate;
    
    if (!navigateFunction) {
      console.error('❌ No navigation function available');
      showNotification('Navigation error - please refresh the page', 'error');
      return;
    }

    if (mode === 'create') {
      console.log('🚀 Navigating to faq_create');
      if (onNavigate) {
        onNavigate('faq_create');
      } else {
        navigate('/faq_create');
      }
    } else if (mode === 'edit') {
      console.log('🚀 Navigating to faq_edit with ID:', faq.id);
      if (onNavigate) {
        onNavigate(`faq_edit/${faq.id}`);
      } else {
        navigate(`/faq_edit/${faq.id}`);
      }
    } else if (mode === 'view') {
      console.log('🚀 Navigating to faq_view with ID:', faq.id);
      if (onNavigate) {
        onNavigate(`faq_view/${faq.id}`);
      } else {
        navigate(`/faq_view/${faq.id}`);
      }
    }
  };

  // Filter FAQ items based on search
  const filteredFaqItems = faqItems.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.seo_keywords.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    fetchFaqItems();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              {/* Back to Dashboard */}
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-800">FAQ Management</h1>
            <p className="text-slate-600">Manage frequently asked questions</p>
          </div>
        </div>
        <button
          onClick={() => {
            console.log('🔍 Add FAQ button clicked');
            openModal('create');
          }}
          className="bg-medical-500 text-white px-4 py-2 rounded-lg hover:bg-medical-600 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add FAQ
        </button>
      </div>

      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`p-4 rounded-lg flex items-center gap-2 ${
              notification.type === 'success' 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}
          >
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            {notification.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search FAQ items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* FAQ Items List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-medical-500 mx-auto"></div>
            <p className="text-slate-600 mt-2">Loading FAQ items...</p>
          </div>
        ) : filteredFaqItems.length === 0 ? (
          <div className="p-8 text-center">
            <HelpCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-800 mb-2">No FAQ Items Found</h3>
            <p className="text-slate-600 mb-4">
              {searchTerm 
                ? `No FAQ items match "${searchTerm}"`
                : "Get started by creating your first FAQ item"
              }
            </p>
            {!searchTerm && (
              <button
                onClick={() => {
                  console.log('🔍 Empty state Create button clicked');
                  openModal('create');
                }}
                className="bg-medical-500 text-white px-4 py-2 rounded-lg hover:bg-medical-600 transition-colors flex items-center gap-2 mx-auto"
              >
                <Plus className="w-4 h-4" />
                Create First FAQ
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-slate-700">Question</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-700">Answer Preview</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-700">Order</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-700">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-700">Featured</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-700">Pages</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredFaqItems.map((faq) => (
                  <motion.tr
                    key={faq.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-800 truncate max-w-xs">
                        {faq.question}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-slate-600 text-sm truncate max-w-xs">
                        {faq.answer.replace(/<[^>]*>/g, '').substring(0, 100)}...
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-slate-600 text-sm">
                        {faq.order}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        faq.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {faq.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {faq.is_featured && (
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-slate-600 text-sm">
                        {faq.associated_pages_list && faq.associated_pages_list.length > 0 
                          ? faq.associated_pages_list.join(', ')
                          : 'All pages'
                        }
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openModal('view', faq)}
                          className="text-slate-600 hover:text-slate-900"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openModal('edit', faq)}
                          className="text-medical-600 hover:text-medical-900"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteFaqItem(faq.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stats */}
      {!loading && faqItems.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total FAQs</p>
                <p className="text-xl font-semibold text-slate-800">{faqItems.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Active</p>
                <p className="text-xl font-semibold text-slate-800">
                  {faqItems.filter(faq => faq.is_active).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Featured</p>
                <p className="text-xl font-semibold text-slate-800">
                  {faqItems.filter(faq => faq.is_featured).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Global FAQs</p>
                <p className="text-xl font-semibold text-slate-800">
                  {faqItems.filter(faq => !faq.associated_pages || faq.associated_pages === '').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}